import { PublicHeader } from "@/components/PublicHeader";
import { Footer } from "@/components/Footer";

/*
  Production-ready Turkish legal content for SuperZeka V1.
  Placeholders (in square brackets) MUST be filled by the company before public launch:
    [ŞİRKET UNVANI]       — legal company name (e.g. "SuperZeka Bilişim A.Ş.")
    [ŞİRKET ADRESİ]       — registered head office address
    [TİCARET SİCİL NO]    — trade registry number
    [MERSİS NO]           — MERSIS number
    [VERGİ DAİRESİ]       — tax office
    [VERGİ NO]            — tax number
    [KEP ADRESİ]          — registered e-mail (KEP)
    [DESTEK E-POSTA]      — support/contact e-mail (e.g. "destek@superzeka.com")
    [KVKK E-POSTA]        — KVKK/DPO e-mail
    [WEB ADRESİ]          — canonical web address (e.g. "https://superzeka.com")
  Nothing else is placeholder — every other line is production content pending legal review.
*/

const COMPANY = {
  name: "[ŞİRKET UNVANI]",
  address: "[ŞİRKET ADRESİ]",
  tradeNo: "[TİCARET SİCİL NO]",
  mersis: "[MERSİS NO]",
  taxOffice: "[VERGİ DAİRESİ]",
  taxNo: "[VERGİ NO]",
  kep: "[KEP ADRESİ]",
  support: "[DESTEK E-POSTA]",
  kvkk: "[KVKK E-POSTA]",
  web: "[WEB ADRESİ]",
};

export function LegalLayout({ title, subtitle, children, testid }) {
  return (
    <div className="min-h-screen bg-white" data-testid={testid}>
      <PublicHeader />
      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
        <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">Yasal</div>
        <h1 className="mt-2 font-display text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900">
          {title}
        </h1>
        {subtitle && <p className="mt-3 text-zinc-600">{subtitle}</p>}
        <div className="mt-10 prose-sz space-y-6 text-[15px]">{children}</div>
        <div className="mt-14 text-xs text-zinc-500">
          Yürürlük tarihi: {new Date().toLocaleDateString("tr-TR")}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function CompanyBlock() {
  return (
    <section>
      <h2>Veri Sorumlusu ve İletişim</h2>
      <p>
        Bu metin bakımından veri sorumlusu ve hizmet sağlayıcı sıfatına haiz kuruluş aşağıda tanıtılmıştır:
      </p>
      <ul>
        <li><strong>Ticari Unvan:</strong> {COMPANY.name}</li>
        <li><strong>Adres:</strong> {COMPANY.address}</li>
        <li><strong>Ticaret Sicil No:</strong> {COMPANY.tradeNo}</li>
        <li><strong>MERSİS No:</strong> {COMPANY.mersis}</li>
        <li><strong>Vergi Dairesi / No:</strong> {COMPANY.taxOffice} / {COMPANY.taxNo}</li>
        <li><strong>KEP Adresi:</strong> {COMPANY.kep}</li>
        <li><strong>Destek İletişim:</strong> {COMPANY.support}</li>
        <li><strong>KVKK İletişim:</strong> {COMPANY.kvkk}</li>
        <li><strong>Web:</strong> {COMPANY.web}</li>
      </ul>
    </section>
  );
}

export function Privacy() {
  return (
    <LegalLayout
      testid="privacy-page"
      title="Gizlilik Politikası"
      subtitle="SuperZeka olarak kişisel verilerinizin gizliliğine ve güvenliğine önem veriyoruz. Bu politika, hangi verileri hangi amaçlarla topladığımızı, nasıl işlediğimizi ve haklarınızı açıklar."
    >
      <CompanyBlock />

      <section>
        <h2>1. Kapsam</h2>
        <p>
          Bu Gizlilik Politikası, SuperZeka platformu (web sitesi, mobil web arayüzü ve API'ler dahil)
          üzerinden sunulan hizmetlerimizi kullanan tüm ziyaretçi, kayıtlı öğrenci ve
          yetkili kullanıcıları kapsar. Politika, 6698 sayılı Kişisel Verilerin Korunması Kanunu
          ("KVKK"), Elektronik Ticaretin Düzenlenmesi Hakkında Kanun ("ETK") ve ilgili ikincil
          mevzuata uygun olarak hazırlanmıştır.
        </p>
      </section>

      <section>
        <h2>2. Topladığımız Kişisel Veriler</h2>
        <ul>
          <li><strong>Kimlik verileri:</strong> ad, soyad.</li>
          <li><strong>İletişim verileri:</strong> e-posta adresi.</li>
          <li><strong>Kimlik doğrulama verileri:</strong> şifre (yalnızca bcrypt algoritmasıyla geri döndürülemez şekilde saklanır), oturum belirteçleri.</li>
          <li><strong>Eğitim verileri:</strong> seçili sınıf seviyesi, tercih edilen ders, sohbet içerikleri, verdiğiniz yanıtlar.</li>
          <li><strong>Kullanım verileri:</strong> günlük mesaj sayısı, oturum açma zamanları, tarayıcı ve cihaz bilgileri, IP adresi.</li>
          <li><strong>Faturalama verileri (yalnızca Premium abonelik durumunda):</strong> ödeme sağlayıcısı tarafından üretilen abonelik/müşteri referans numaraları. Kart bilgileri hiçbir zaman SuperZeka'ya iletilmez; doğrudan lisanslı ödeme sağlayıcısı (iyzico) tarafından işlenir.</li>
        </ul>
      </section>

      <section>
        <h2>3. İşleme Amaçlarımız</h2>
        <ul>
          <li>Hizmetin sunulması, kullanıcı hesabının oluşturulması ve yönetilmesi</li>
          <li>Sınıf ve derse göre kişiselleştirilmiş içerik üretimi</li>
          <li>Kimlik doğrulama, hesap güvenliği ve dolandırıcılığın önlenmesi</li>
          <li>Kullanım limitlerinin uygulanması ve sistem sağlığının izlenmesi</li>
          <li>Yasal yükümlülüklerin yerine getirilmesi</li>
          <li>Ürünün iyileştirilmesi için toplu (anonimleştirilmiş) analiz</li>
          <li>Sizinle iletişim kurulması (hoş geldin, e-posta doğrulama, şifre sıfırlama, hizmet bildirimleri)</li>
        </ul>
      </section>

      <section>
        <h2>4. İşleme Hukuki Sebepleri</h2>
        <p>
          Kişisel verileriniz KVKK m.5/2 kapsamında sözleşmenin kurulması veya ifası için gerekli olması,
          hukuki yükümlülüğün yerine getirilmesi, meşru menfaat ve gerektiğinde açık rızanız hukuki
          sebeplerine dayanılarak işlenir.
        </p>
      </section>

      <section>
        <h2>5. Veri Aktarımı</h2>
        <p>
          Verileriniz yalnızca hizmetin sunulması için zorunlu olan iş ortaklarıyla ve
          sözleşme çerçevesinde paylaşılabilir:
        </p>
        <ul>
          <li>Barındırma altyapısı sağlayıcıları (bulut ve veri tabanı)</li>
          <li>Yapay zeka hizmet sağlayıcısı (yalnızca eğitim amaçlı yanıt üretimi için, mesaj içeriği aktarılır; gizlilik ve saklama politikalarına tabidir)</li>
          <li>E-posta iletim sağlayıcısı (hesap doğrulama, şifre sıfırlama, hizmet bildirimleri için ad ve e-posta adresi aktarılır)</li>
          <li>Ödeme hizmeti sağlayıcısı (yalnızca Premium abonelik durumunda; kart verileri sağlayıcıda kalır)</li>
        </ul>
        <p>
          Yurt dışına veri aktarımı gerektiğinde KVKK m.9 gereklerine ve varsa Kurul kararlarına
          uygun hareket edilir.
        </p>
      </section>

      <section>
        <h2>6. Saklama Süresi</h2>
        <p>
          Kişisel verileriniz, işlenme amaçları geçerli olduğu ve ilgili mevzuatın öngördüğü
          asgari saklama süreleri boyunca saklanır. Hesabınızı silmeniz halinde kimlik ve
          sohbet verileriniz makul süre içinde silinir veya anonim hâle getirilir; ancak
          fatura, log ve yasal savunma amaçlı verileri ilgili mevzuatın öngördüğü süre kadar
          saklamamız gerekebilir.
        </p>
      </section>

      <section>
        <h2>7. Güvenlik Önlemleri</h2>
        <ul>
          <li>Tüm trafik TLS üzerinden şifrelenmiştir.</li>
          <li>Şifreler bcrypt ile geri döndürülemez şekilde saklanır.</li>
          <li>Oturum belirteçleri (JWT) yalnızca sunucu tarafında imzalanır; kritik cookie'ler HttpOnly ve SameSite bayraklarıyla korunur.</li>
          <li>Rol tabanlı erişim kontrolü (öğrenci/yönetici) uygulanır.</li>
          <li>Kişisel verilere erişim yalnızca yetkilendirilmiş personelle sınırlıdır ve gerekçelendirilir.</li>
        </ul>
      </section>

      <section>
        <h2>8. Çerezler</h2>
        <p>
          SuperZeka; oturum yönetimi ve temel güvenlik amaçlarıyla zorunlu çerezler kullanır.
          Reklam veya üçüncü taraf takip çerezleri kullanılmamaktadır. Tarayıcı ayarlarınızdan
          çerezleri sınırlandırabilirsiniz; bu durumda bazı özellikler çalışmayabilir.
        </p>
      </section>

      <section>
        <h2>9. Reşit Olmayan Kullanıcılar</h2>
        <p>
          SuperZeka, öğrencilerin eğitim ihtiyaçlarına yönelik bir platformdur. 18 yaşından
          küçük kullanıcıların hesap oluşturmadan önce veli/vasi bilgilendirmesi ve rızası
          alınmalıdır. Veli veya vasiler, çocuklarına ait bir hesabın silinmesini
          <strong> {COMPANY.kvkk}</strong> adresine yazarak talep edebilir.
        </p>
      </section>

      <section>
        <h2>10. Haklarınız</h2>
        <p>KVKK m.11 kapsamında;</p>
        <ul>
          <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme,</li>
          <li>İşlenmişse buna ilişkin bilgi talep etme,</li>
          <li>İşlenme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme,</li>
          <li>Yurt içinde ya da yurt dışında kişisel verilerin aktarıldığı üçüncü kişileri bilme,</li>
          <li>Eksik ya da yanlış işlenen verilerin düzeltilmesini isteme,</li>
          <li>KVKK m.7 çerçevesinde silinmesini veya yok edilmesini isteme,</li>
          <li>Otomatik sistemler vasıtasıyla analiz edilmesi sonucu aleyhinize bir sonucun ortaya çıkmasına itiraz etme,</li>
          <li>Kanuna aykırı işleme sebebiyle zarara uğramanız hâlinde zararın giderilmesini talep etme haklarına sahipsiniz.</li>
        </ul>
        <p>
          Bu haklarınızı kullanmak için taleplerinizi <strong>{COMPANY.kvkk}</strong> adresine yazılı
          olarak iletebilirsiniz. Talebiniz en geç 30 gün içinde ücretsiz olarak sonuçlandırılır.
        </p>
      </section>

      <section>
        <h2>11. Güncellemeler</h2>
        <p>
          Bu politika mevzuat veya hizmet değişikliklerine bağlı olarak güncellenebilir. Güncel
          sürüm her zaman bu sayfadan erişilebilir olacaktır; önemli değişikliklerde ayrıca
          bilgilendirme yapılır.
        </p>
      </section>
    </LegalLayout>
  );
}

export function Terms() {
  return (
    <LegalLayout
      testid="terms-page"
      title="Kullanım Şartları"
      subtitle="SuperZeka platformunu kullanmadan önce lütfen bu şartları dikkatlice okuyunuz. Hesap oluşturmak ve hizmeti kullanmak, bu şartların tamamını kabul ettiğiniz anlamına gelir."
    >
      <CompanyBlock />

      <section>
        <h2>1. Taraflar ve Konu</h2>
        <p>
          İşbu Kullanım Şartları, {COMPANY.name} ("SuperZeka") tarafından işletilen platformun
          kullanımına ilişkin kural ve koşulları düzenler. Platformu ziyaret eden veya kayıt
          olan tüm kullanıcılar bu şartlara uymayı kabul eder.
        </p>
      </section>

      <section>
        <h2>2. Hizmet Tanımı</h2>
        <p>
          SuperZeka; LGS, TYT ve AYT sayısal sınavlarına hazırlanan öğrencilere yönelik yapay zeka destekli bir
          öğrenme asistanı sunar. Kullanıcılar sınıf seviyelerini ve ders tercihlerini belirleyerek
          sorularını sorabilir; sistem konuları adım adım açıklar, örneklerle pekiştirir ve
          kavrama sorularıyla anlamayı destekler.
        </p>
      </section>

      <section>
        <h2>3. Hesap ve Güvenlik</h2>
        <ul>
          <li>Kayıt sırasında verdiğiniz bilgilerin doğru, güncel ve size ait olduğunu taahhüt edersiniz.</li>
          <li>Hesap şifrenizin gizliliğinden ve hesabınız üzerinden yapılan tüm işlemlerden yalnızca siz sorumlusunuz.</li>
          <li>Yetkisiz erişim veya güvenlik ihlali şüphenizi derhal <strong>{COMPANY.support}</strong> adresine bildirmeniz gerekir.</li>
          <li>SuperZeka; güvenlik gerektiren durumlarda hesabınızı geçici olarak askıya alma veya devre dışı bırakma hakkını saklı tutar.</li>
        </ul>
      </section>

      <section>
        <h2>4. Kabul Edilebilir Kullanım</h2>
        <p>Platformu aşağıdaki amaçlarla kullanmak yasaktır:</p>
        <ul>
          <li>Yürürlükteki mevzuata veya kamu düzenine aykırı içerik üretmek, paylaşmak veya talep etmek.</li>
          <li>Sınavlarda, ödevlerde veya değerlendirmelerde başkalarının yerine cevap üretmek (akademik dolandırıcılık).</li>
          <li>Hakaret, tehdit, taciz, ayrımcılık, cinsel içerik, şiddet veya nefret söylemi içeren metinler oluşturmak.</li>
          <li>Kişisel verileri veya telif hakkına konu içerikleri hukuka aykırı biçimde işlemek.</li>
          <li>Platformun altyapısına zarar vermeye veya yapay yollarla limit aşımına yol açmaya çalışmak.</li>
        </ul>
        <p>
          Bu kuralları ihlal eden hesaplar önceden bildirimde bulunulmaksızın askıya alınabilir
          ya da kapatılabilir.
        </p>
      </section>

      <section>
        <h2>5. Ücretsiz ve Premium Plan</h2>
        <ul>
          <li><strong>Ücretsiz Plan:</strong> Sınırlı günlük mesaj hakkı ve tüm ders/sınıf seviyelerine erişim sunulur.</li>
          <li><strong>Premium Plan:</strong> Yüksek günlük mesaj limiti ve öncelikli erişim sağlanır. Fiyatlandırma abonelik başlangıcında açıkça belirtilir.</li>
          <li>Ödeme; lisanslı ödeme kuruluşu (iyzico) üzerinden gerçekleşir; kart bilgileri SuperZeka tarafından saklanmaz.</li>
          <li>Abonelik başlangıcından itibaren ilgili mevzuat çerçevesindeki cayma hakkınız saklıdır. Dijital hizmetlerin ifasına başlanmış olması hâlinde cayma hakkı mevzuatta belirtilen istisnalara tâbi olabilir.</li>
          <li>Aboneliğinizi Panel &gt; Abonelik ekranı üzerinden veya <strong>{COMPANY.support}</strong> adresine e-posta göndererek istediğiniz zaman iptal edebilirsiniz.</li>
        </ul>
      </section>

      <section>
        <h2>6. Yapay Zeka İçeriği ve Doğruluk</h2>
        <p>
          SuperZeka yanıtları büyük dil modelleri tarafından üretilir. İçerikler bilgilendirme
          amaçlıdır ve zaman zaman hata içerebilir. Sınav başarısı, not artışı veya kesin doğruluk
          garantisi verilmez. Önemli akademik kararlarınızda öğretmeninizin görüşünü almanız
          önerilir.
        </p>
      </section>

      <section>
        <h2>7. Fikri Mülkiyet</h2>
        <p>
          Platformun tasarımı, logosu, kaynak kodu, veri tabanı yapıları ve marka
          unsurları SuperZeka'ya aittir. İzinsiz kopyalanması, çoğaltılması, dağıtılması veya
          tersine mühendisliğe konu edilmesi yasaktır. Kullanıcı tarafından oluşturulan sorular
          ve verilen içeriklerin mülkiyeti kullanıcıda kalır; ancak SuperZeka, hizmetin
          sunulması, hata giderme ve toplu (anonim) analiz için gerekli süreyle sınırlı olmak
          üzere bu içerikleri işleme hakkına sahiptir.
        </p>
      </section>

      <section>
        <h2>8. Sorumluluğun Sınırlandırılması</h2>
        <p>
          SuperZeka; ağır kusur veya kasıt hâlleri dışında, hizmetin kesintisiz veya hatasız
          çalışacağını taahhüt etmez ve dolaylı, arızi veya sonuç zararlarından sorumlu tutulamaz.
          Toplam sorumluluk, kullanıcının ilgili aboneliği için ödediği son on iki aylık ücret
          ile sınırlıdır.
        </p>
      </section>

      <section>
        <h2>9. Değişiklikler ve Fesih</h2>
        <p>
          SuperZeka, bu şartları güncelleyebilir; önemli değişikliklerde uygun yöntemlerle
          bilgilendirme yapılır. Şartlara uymayan kullanıcıların hesabı feshedilebilir.
        </p>
      </section>

      <section>
        <h2>10. Uygulanacak Hukuk ve Uyuşmazlık Çözümü</h2>
        <p>
          İşbu şartlar Türkiye Cumhuriyeti hukukuna tabidir. Doğabilecek uyuşmazlıklarda
          İstanbul Merkez (Çağlayan) Mahkemeleri ve İcra Daireleri yetkilidir; tüketici
          uyuşmazlıklarında ilgili tüketici hakem heyetleri ve tüketici mahkemelerinin yetkisi
          saklıdır.
        </p>
      </section>

      <section>
        <h2>11. İletişim</h2>
        <p>
          Bu şartlarla ilgili sorularınızı <strong>{COMPANY.support}</strong> adresine iletebilirsiniz.
        </p>
      </section>
    </LegalLayout>
  );
}

export function KVKK() {
  return (
    <LegalLayout
      testid="kvkk-page"
      title="KVKK Aydınlatma Metni"
      subtitle={`6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") kapsamında SuperZeka tarafından yapılan kişisel veri işleme faaliyetleri hakkında sizi aydınlatmak amacıyla hazırlanmıştır.`}
    >
      <CompanyBlock />

      <section>
        <h2>1. Amaç ve Kapsam</h2>
        <p>
          Bu aydınlatma metni, KVKK m.10 uyarınca, veri sorumlusu sıfatı taşıyan {COMPANY.name}
          tarafından yürütülen kişisel veri işleme faaliyetleri hakkında öğrenci ve kullanıcıları
          bilgilendirmek amacıyla hazırlanmıştır.
        </p>
      </section>

      <section>
        <h2>2. İşlenen Kişisel Veri Kategorileri</h2>
        <ul>
          <li><strong>Kimlik verisi:</strong> Ad, soyad.</li>
          <li><strong>İletişim verisi:</strong> E-posta adresi.</li>
          <li><strong>Kimlik doğrulama verisi:</strong> Bcrypt ile hashlenmiş şifre, oturum belirteçleri, e-posta doğrulama ve şifre sıfırlama tokenları.</li>
          <li><strong>Eğitim verisi:</strong> Sınıf seviyesi, ders tercihi, sohbet başlıkları ve içerikleri.</li>
          <li><strong>Müşteri işlem verisi:</strong> Günlük kullanım sayaçları, ürün geri bildirimleri.</li>
          <li><strong>İşlem güvenliği verisi:</strong> IP adresi, tarayıcı bilgisi, oturum kayıtları.</li>
          <li><strong>Finansal veri (yalnızca Premium):</strong> Ödeme sağlayıcısı tarafından üretilen abonelik ve müşteri referans numaraları. Kart bilgileri SuperZeka'ya iletilmez.</li>
        </ul>
      </section>

      <section>
        <h2>3. Kişisel Verilerin Toplanma Yöntemi</h2>
        <p>
          Verileriniz; kayıt formu, giriş ve profil güncelleme akışları, sohbet arayüzü,
          otomatik sistem logları ve ödeme sağlayıcısıyla yapılan yönlendirme akışları
          aracılığıyla elektronik ortamda toplanır.
        </p>
      </section>

      <section>
        <h2>4. İşleme Amaçları</h2>
        <ul>
          <li>Hizmetin sunulması ve sözleşmenin ifası</li>
          <li>Kimlik doğrulama, hesap güvenliği ve dolandırıcılığın önlenmesi</li>
          <li>Kullanım limitleri ve abonelik yönetimi</li>
          <li>Kullanıcı deneyiminin iyileştirilmesi</li>
          <li>Yasal yükümlülüklerin yerine getirilmesi</li>
          <li>Hukuki uyuşmazlıkların çözümü ve savunma hakkının kullanılması</li>
          <li>İletişim faaliyetleri (hoş geldin, e-posta doğrulama, şifre sıfırlama, hizmet duyuruları)</li>
        </ul>
      </section>

      <section>
        <h2>5. Hukuki Sebepler (KVKK m.5)</h2>
        <ul>
          <li>Sözleşmenin kurulması veya ifası için gerekli olması</li>
          <li>Kanunlarda açıkça öngörülmesi ve hukuki yükümlülüğün yerine getirilmesi</li>
          <li>Bir hakkın tesisi, kullanılması veya korunması</li>
          <li>Meşru menfaatler için veri işlenmesinin zorunlu olması</li>
          <li>Gerekli hallerde ilgili kişinin açık rızası</li>
        </ul>
      </section>

      <section>
        <h2>6. Aktarım</h2>
        <p>
          Kişisel verileriniz KVKK m.8 ve m.9 çerçevesinde; hizmetin sunulması için gerekli
          barındırma, yapay zeka, e-posta ve ödeme hizmet sağlayıcılarına, yasal talep hâlinde
          yetkili kamu kurum ve kuruluşlarına aktarılabilir. Yurt dışına aktarım, Kurul
          kararlarına ve mevzuata uygun olarak gerçekleştirilir.
        </p>
      </section>

      <section>
        <h2>7. Saklama Süresi</h2>
        <p>
          Kişisel verileriniz işleme amacı devam ettiği ve ilgili mevzuatta öngörülen asgari
          süreler boyunca saklanır. Hesap silme talebiniz üzerine kimlik ve sohbet verileriniz
          makul süre içinde silinir veya anonimleştirilir; log ve fatura verileri yasal saklama
          süresi sonuna kadar tutulabilir.
        </p>
      </section>

      <section>
        <h2>8. Haklarınız (KVKK m.11)</h2>
        <ul>
          <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme</li>
          <li>İşlenmişse bilgi talep etme</li>
          <li>İşlenme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme</li>
          <li>Aktarıldığı üçüncü kişileri bilme</li>
          <li>Eksik veya yanlış işlenen verilerin düzeltilmesini isteme</li>
          <li>Silme veya yok etme talebinde bulunma</li>
          <li>Düzeltme, silme veya yok etme işlemlerinin aktarım yapılan üçüncü kişilere bildirilmesini isteme</li>
          <li>Otomatik sistemler vasıtasıyla analiz edilmesi neticesinde aleyhinize doğan sonuca itiraz etme</li>
          <li>Hukuka aykırı işleme nedeniyle uğradığınız zararın giderilmesini talep etme</li>
        </ul>
      </section>

      <section>
        <h2>9. Başvuru Yöntemi</h2>
        <p>
          Yukarıdaki haklarınızı kullanmak için başvurularınızı; kimliğinizi tevsik edici bilgi
          ve belgelerle birlikte <strong>{COMPANY.kvkk}</strong> adresine e-posta ile veya
          <strong> {COMPANY.address}</strong> adresine yazılı olarak iletebilirsiniz. Başvurunuz
          en geç 30 gün içinde ücretsiz olarak yanıtlanır; işlem ayrıca bir maliyet gerektiriyorsa
          Kurul'un belirlediği tarifedeki ücret talep edilebilir.
        </p>
      </section>

      <section>
        <h2>10. Reşit Olmayan Kullanıcılar</h2>
        <p>
          18 yaşından küçük kullanıcıların hesap oluşturmadan önce veli/vasi bilgisi ve rızası
          alınmalıdır. Veli veya vasiler, çocuklarına ait bir hesabın silinmesini veya verilerin
          işlenmesine yönelik taleplerini <strong>{COMPANY.kvkk}</strong> adresine iletebilir.
        </p>
      </section>
    </LegalLayout>
  );
}
